import React from 'react';
import { Info, Users, Shield, Key, Clock, UserCheck, AlertCircle } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface AccessLevelInfoProps {
  level: 'open' | 'restricted' | 'closed';
}

export const AccessLevelInfo: React.FC<AccessLevelInfoProps> = ({ level }) => {
  const getAccessLevelDetails = (accessLevel: string) => {
    switch (accessLevel) {
      case 'open':
        return {
          icon: <Users className="h-4 w-4 text-green-500" />,
          title: 'Open Access',
          description: 'Anyone with the game code can join immediately',
          process: [
            'Player enters game code',
            'Instantly added to game roster',
            'No approval needed',
            'Notifications sent to coaches'
          ],
          permissions: 'All roles can join (Player, Coach, Scorekeeper)',
          waitTime: 'Immediate',
          notifications: 'Coach and team owners are notified when someone joins',
          restrictions: 'None - open to everyone'
        };
      case 'restricted':
        return {
          icon: <Shield className="h-4 w-4 text-yellow-500" />,
          title: 'Restricted Access',
          description: 'Players can request to join, but require approval',
          process: [
            'Player enters game code',
            'Join request is submitted',
            'Coach/Team Owner receives notification',
            'Approval required before adding to roster',
            'Player notified of approval/rejection'
          ],
          permissions: 'Configurable - set specific roles, age groups, teams',
          waitTime: 'Depends on approval time (typically within 24 hours)',
          notifications: 'All stakeholders notified at each step',
          restrictions: 'Based on your configuration settings'
        };
      case 'closed':
        return {
          icon: <Key className="h-4 w-4 text-red-500" />,
          title: 'Closed Access',
          description: 'No new participants can join',
          process: [
            'Game code is deactivated',
            'New join attempts are blocked',
            'Existing participants remain',
            'Only manual roster changes allowed'
          ],
          permissions: 'No new joins allowed',
          waitTime: 'N/A - joining disabled',
          notifications: 'None - no join attempts possible',
          restrictions: 'Complete lockdown of new participants'
        };
      default:
        return {
          icon: <AlertCircle className="h-4 w-4 text-muted-foreground" />,
          title: 'Unknown',
          description: 'Unknown access level',
          process: [],
          permissions: 'Unknown',
          waitTime: 'Unknown',
          notifications: 'Unknown',
          restrictions: 'Unknown'
        };
    }
  };

  const details = getAccessLevelDetails(level);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground">
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center gap-2">
            {details.icon}
            <div>
              <h4 className="font-semibold text-sm">{details.title}</h4>
              <p className="text-xs text-muted-foreground">{details.description}</p>
            </div>
          </div>

          {/* Process Flow */}
          <div>
            <h5 className="font-medium text-xs mb-2 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              How it works:
            </h5>
            <ol className="text-xs space-y-1 text-muted-foreground">
              {details.process.map((step, index) => (
                <li key={index} className="flex gap-2">
                  <span className="font-medium text-primary">{index + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 gap-2 text-xs">
            <div>
              <span className="font-medium">Permissions:</span>
              <p className="text-muted-foreground">{details.permissions}</p>
            </div>
            <div>
              <span className="font-medium">Wait Time:</span>
              <p className="text-muted-foreground">{details.waitTime}</p>
            </div>
            <div>
              <span className="font-medium">Notifications:</span>
              <p className="text-muted-foreground">{details.notifications}</p>
            </div>
            <div>
              <span className="font-medium">Restrictions:</span>
              <p className="text-muted-foreground">{details.restrictions}</p>
            </div>
          </div>

          {/* Status Badge */}
          <div className="pt-2 border-t">
            <Badge 
              variant={
                level === 'open' ? 'default' :
                level === 'restricted' ? 'secondary' : 'destructive'
              }
              className="text-xs"
            >
              Currently: {level.toUpperCase()}
            </Badge>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};